import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { RCCButton, RCCColorPicker, RCCGrid, RCCInput, RCCRefModalProps, RCCSwitch, RCCTextArea, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import Select from "src/components/Select";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors } from "src/utils/errors";
import { css } from "styled-components";
import { v4 } from "uuid";
import { IColumnsTab, IRefconfigColumnModal, IWdtba011 } from "../../../interface";
import { rulesSchema } from "../../../schemas";
import { colorPickerStyle, FieldAlwaysOnRight, FieldOnRight } from "../../../styles";

const ConfigColumnModal: React.ForwardRefRenderFunction<IRefconfigColumnModal,IColumnsTab> = ({
	columns,
	setColumns,
	control,
	register,
	setValue,
	unregister,
	getValues 
}, ref) => {
	const { translate} = useTranslation();

	const { 
		formState:{errors:rulesErrors}, 
		setError,
		clearErrors
	}= useForm();
	
	const [subTitle, setSubTitle] = useState<string | undefined>(undefined);
	const [columnIndex, setColumnIndex] = useState<number>(0);
	const modalconfig = useRef<RCCRefModalProps>(null);
	const [rules,setRules] = useState<IWdtba011[]>([] as IWdtba011[]);
	const [originalRules,setOriginalRules] = useState<IWdtba011[]>([] as IWdtba011[]);

	

	const append = () => {
		const newObj = 
		{
			id:v4(),
			wda011Id:0,
			wda011Negrito:false,
			wda011Italico:false,
			showConfig:true
		} as IWdtba011;
			
		setRules((prevState) => {
			const formValues = getValues(`columns.${columnIndex}.rules`) as IWdtba011[];
			const newRules = prevState.map((x) => {
				const rule = formValues.find((y) => y.id === x.id); 
				if(rule){
					return {...x,...rule};
				}
				return x;
			});
			newRules.push(newObj);
			setValue(`columns.${columnIndex}.rules`,[...newRules]);
			return newRules;
		});
	};

	const remove = (id: string) => {
		
		const formValues = getValues(`columns.${columnIndex}.rules`) as IWdtba011[];
		const newRules = rules.map((x) => {
			const rule = formValues.find((y) => y.id === x.id); 
			if(rule){
				return {...x,...rule};
			}
			return x;
			
		}).filter((x) => x.id!==id);

		unregister(`columns.${columnIndex}.rules`);
		setValue(`columns.${columnIndex}.rules`,[...newRules]);
		setRules(newRules);
	};

	const onSubmit = async() => { 
		try {
			modalconfig.current?.setLoadingButton();
			clearErrors();
			const formValues = getValues(`columns.${columnIndex}.rules`) as IWdtba011[];
			const newRules = rules.map((x) => {
				const rule = formValues.find((y) => y.id === x.id); 
				if(rule){
					return {...x,...rule};
				}
				return x;
			});

			await rulesSchema(translate).validate(newRules, {
				abortEarly: false
			});

			setValue(`columns.${columnIndex}.rules`,[...newRules]);
			setRules(newRules);

			setColumns((prevState) => 
				prevState.map((col,index) => {
					if(columnIndex!==index)
						return col;
					return {...col,rules:newRules};
				})
			);

			modalconfig.current?.resetLoadingButton();
			modalconfig.current?.closeModal();
		} catch (error) {
			modalconfig.current?.resetLoadingButton();
			getValidationErrors(error,setError);
		}	
		
	};

	const onClose = () => { 
		unregister(`columns.${columnIndex}.rules`);
		setValue(`columns.${columnIndex}.rules`,originalRules);
		setRules(originalRules);
		setColumns((prevState) => 
			prevState.map((col,index) => {
				if(columnIndex!==index)
					return col;
				return {...col,rules:originalRules};
			})
		);
	};

	const returnOptions = () => columns.map((col) =>
		({
			value:col.id,
			label:`${col.wda003Label} (${col.wda003Nomecoluna})`
		})
	);

	const actionOpen = (index:number,subtitle:string) => {
		setSubTitle(subtitle);
		setColumnIndex(index);
		modalconfig.current?.openModal();
	};

	const updateState = (field:"wda011Operador"|"wda011Tamanhofonte"|"wda011Negrito"|"wda011Italico"|"wda011Corfonte"|"wda011Avancado"|"refColumnId"|"showConfig",value:any,id:string) => {
		
		setRules((prevState) => prevState.map((x) => {
			if(x.id===id){
				const newField:IWdtba011 = {...x} ;
				newField[field] = value;
				return newField;
			}
			return x;
		}));
	};
	
	useImperativeHandle(ref, () => ({
		actionOpen
	}));

	useEffect(() => {
		if(columnIndex){
			setRules(columns[columnIndex]?.rules||[]);
			setOriginalRules(columns[columnIndex]?.rules||[]);
		}
		else{
			setRules([]);
			setOriginalRules([]);
		}
		
	}, [columnIndex]);

	return (
		<Modal
			ref={modalconfig}
			header={{
				title:"Configurar coluna",
				icon: {
					name: "chartBar"
				},
				subtitle: `${translate("Coluna")}:${subTitle}` 
			}}
			onClose={onClose}
			onSubmit={onSubmit}
			withoutForm
		>	
			<RCCGrid container item 
				justifyContent="center"
			>
				<RCCGrid container item 
					xs={12}
				>
					<RCCGrid container justifyContent="space-between" alignContent="flex-end" wrap="nowrap">
						<RCCGrid container item justifyContent="flex-end">
							<RCCTooltip
								text={translate("Adicionar Regra")}
								color="purple"
								position="left">
								<RCCButton
									icon={{name: "plus"}}
									color="purple"
									onClick={() => {
										append();
									}}
									layout="isRounded"
									size="sm"
								/>	
							</RCCTooltip>
						</RCCGrid>
					</RCCGrid>
					<RCCGrid container item>
						{rules.map((rule,index) => (
							<RCCGrid container item key={v4()}>
								
								<input 
									{...register(`columns.${columnIndex}.rules[${index}].id`)} 
									name={`columns.${columnIndex}.rules[${index}].id`}
									value={rule.id}
									type="hidden"
								/>
								<input 
									{...register(`columns.${columnIndex}.rules[${index}].wda011Id`)} 
									name={`columns.${columnIndex}.rules[${index}].wda011Id`}
									value={rule.wda011Id}
									type="hidden"
								/>
								<RCCGrid container item 
									md={3}
									sm={6}
									xs={12}
									styleInline={FieldOnRight}
								>
									<Select
										name={`columns.${columnIndex}.rules[${index}].refColumnId`}
										label={translate("Coluna de referência")}
										defaultValue={rule.refColumnId}
										error = {rulesErrors && rulesErrors[index]?.refColumnId}
										clearable
										options={returnOptions()}
										control={control}
										onChangeCapture={(e) => updateState("refColumnId",e.value,rule.id)}
									/>
								</RCCGrid>
								<RCCGrid container item 
									md={3}
									sm={6}
									xs={12}
									styleInline={FieldOnRight}
								>
									<Select
										name={`columns.${columnIndex}.rules[${index}].wda011Operador`}
										label={translate("Operador da formatação")}
										defaultValue={rule.wda011Operador?.toString()}
										error = {rulesErrors && rulesErrors[index]?.wda011Operador}
										required
										options={[
											{ value: "1", label: translate("Igual") },
											{ value: "2", label: translate("Diferente") },
											{ value: "3", label: translate("Maior que") },
											{ value: "4", label: translate("Menor que") },
											{ value: "5", label: translate("Maior ou igual") },
											{ value: "6", label: translate("Menor ou igual") },
											{ value: "7", label: translate("Entre ... e ...") },
											{ value: "8", label: translate("Na lista") },
											{ value: "9", label: translate("É Nulo") },
											{ value: "10", label: translate("Contem") },
											{ value: "11", label: translate("Não entre ... e ...") },
											{ value: "12", label: translate("Fora da lista") },
											{ value: "13", label: translate("Não é nulo") },
											{ value: "14", label: translate("Não contem") },
											{ value: "15", label: translate("Inicia com") },
											{ value: "16", label: translate("Termina com") }
										]}
										control={control}
										onChangeCapture={(e) => updateState("wda011Operador",parseInt(e.value,10),rule.id)}
									/>
								</RCCGrid>
								<RCCGrid container item 
									md={4}
									sm={10}
									xs={9}
									styleInline={FieldOnRight}
									wrap="nowrap"
								>
									<RCCGrid container item> 
										<RCCInput
											name={`columns.${columnIndex}.rules[${index}].wda011Valor`}
											label={translate("Valor")}
											register={register}
											defaultValue={rule.wda011Valor}
											error = {rulesErrors && rulesErrors[index]?.wda011Valor}
											required = {rule.wda011Operador?.toString() !== "7" && rule.wda011Operador?.toString() !== "11"}
											maxLength={200}
										/>
									</RCCGrid>
									{(rule.wda011Operador?.toString() === "7"||rule.wda011Operador?.toString() === "11") && (
										<RCCGrid container item 
											styleInline={FieldAlwaysOnRight}> 
											<RCCInput
												name={`columns.${columnIndex}.rules[${index}].wda011Valorfim`}
												label={translate("Valor final")}
												register={register}
												defaultValue={rule.wda011Valorfim}
												error = {rulesErrors && rulesErrors[index]?.wda011Valorfim}
												required
												maxLength={200}
											/>
										</RCCGrid>
									)
									}
									
								</RCCGrid>
								<RCCGrid container item
									xs={3}
									sm={2}
									styleInline={FieldOnRight}
									alignContent="center"
								>
									<RCCTooltip
										text={translate("Configurações")}
										color={rule.showConfig ? "blue":"grey"}
									>
										<RCCButton 
											size="sm" 
											color={rule.showConfig ? "blue":"grey"}
											icon={{name:"settings"}} 
											layout="isRounded"
											onClick={() => {
												updateState("showConfig",!rule.showConfig,rule.id);
											}}
										/>
									</RCCTooltip>
									<RCCTooltip
										text={translate("Excluir regra")}
										color="red"
										position="left">
										<RCCButton
											icon={{name: "trash"}}
											color="red"
											onClick={() => {remove(rule.id);}}
											layout="isRounded"
											size="sm"
										/>	
									</RCCTooltip>
								</RCCGrid>
								{rule.showConfig && 
									<>
										<RCCGrid container item 
											xs={12}
											styleInline={FieldOnRight}
										>
											<RCCGrid container item 
												sm={4}
												xs={12}
												styleInline={FieldOnRight}
											>
												<Select
													name={`columns.${columnIndex}.rules[${index}].wda011Tamanhofonte`}
													label={translate("Tamanho da fonte")}
													defaultValue={rule.wda011Tamanhofonte?.toString()}
													error = {rulesErrors && rulesErrors[index]?.wda011Tamanhofonte}
													options={[
														{ value: "8", label: "8" },
														{ value: "9", label: "9" },
														{ value: "10", label: "10" },
														{ value: "11", label: "11" },
														{ value: "12", label: "12" },
														{ value: "14", label: "14" },
														{ value: "16", label: "16" },
														{ value: "18", label: "18" },
														{ value: "20", label: "20" },
														{ value: "22", label: "22" },
														{ value: "24", label: "24" },
														{ value: "26", label: "26" },
														{ value: "28", label: "28" },
														{ value: "30", label: "30" }
													]}
													onChangeCapture={(e) => updateState("wda011Tamanhofonte",parseInt(e.value,10),rule.id)}
													control={control}
												/>      
											</RCCGrid>
											<RCCGrid container item 
												sm={4}
												xs={6}
												styleInline={FieldOnRight}
											>
												<RCCSwitch
													name={`columns.${columnIndex}.rules[${index}].wda011Italico`}
													label={translate("Itálico")}
													register={register}
													defaultChecked={rule.wda011Italico}
													error = {rulesErrors && rulesErrors[index]?.wda011Italico}
												/>
												<RCCSwitch
													name={`columns.${columnIndex}.rules[${index}].wda011Negrito`}
													label={translate("Negrito")}
													register={register}
													defaultChecked={rule.wda011Negrito}
													error = {rulesErrors && rulesErrors[index]?.wda011Negrito}
												/>
											</RCCGrid>
											<RCCGrid container item 
												sm={4}
												xs={6}
												styleInline={css`padding-bottom:5px; padding-left:0.313rem;`} alignItems="center"
											>
												<RCCColorPicker
													name={`columns.${columnIndex}.rules[${index}].wda011Corfonte`}
													control={control}
													label={translate("Cor da fonte")}
													labelPosition="start"
													colorPickerProps ={{color:rule.wda011Corfonte||"#ffffff0"}}
													defaultValue = {rule.wda011Corfonte}
													styleInline={colorPickerStyle}
												/>
												<RCCTooltip
													text={translate("Limpar cor")}
													color="yellow"	
												>
													<RCCButton 
														onClick={() => {
															updateState("wda011Corfonte","",rule.id);
															setValue(`columns[${columnIndex}].rules[${index}].wda011Corfonte`,"");
														}}
														size="xs" 
														color="transparent" 
														icon={{
															name:"broom",
															size:14,
															color: "var(--yellow)"
														}} 
														layout="isRounded"
													/>
												</RCCTooltip>
											</RCCGrid>
										</RCCGrid>
										<RCCGrid container item 
											xs={12}
											styleInline={FieldOnRight}
										>
											<RCCTextArea 
												name={`columns.${columnIndex}.rules[${index}].wda011Avancado`}
												label={`${translate("Formatação avançada")} (HTML)`}
												defaultValue={rule.wda011Avancado}
												error = {rulesErrors&& rulesErrors[index]?.wda011Avancado}
												register={register}
												rows={3}
											/>
										</RCCGrid>
										{/*
											icones comentados por falta de compatibilidade com o sistema antigo
										*/}
										{/* <RCCGrid container item 
											xs={12}
											styleInline={FieldOnRight}
										>
											<RCCGrid container item 
												sm={4}
												xs={12}
												styleInline={FieldOnRight}
											>
												<IconPicker
													setIconValue={(value) => {setValue(`columns[${columnIndex}].rules[${index}].wda011Corprefixo`,value);}}
													label={translate("Icone de prefixo")}
													// defaultValue={rule.wda011Iconeprefixo}
												/>
											</RCCGrid>
											<RCCGrid container item 
												sm={4}
												xs={12}
												styleInline={FieldOnRight}
											>
												<RCCColorPicker
													name={`columns[${columnIndex}].rules[${index}].wda011Corprefixo`}
													control={control}
													label={translate("Cor do prefixo")}
													labelPosition="start"
													colorPickerProps ={{color:rule.wda011Corprefixo||"#ffffff0"}}
													defaultValue = {rule.wda011Corprefixo}
												/>
											</RCCGrid>
										</RCCGrid>
										<RCCGrid container item 
											xs={12}
											styleInline={FieldOnRight}
										>
											<RCCGrid container item 
												sm={4}
												xs={12}
												styleInline={FieldOnRight}
											>
												<IconPicker
													setIconValue={(value) => {setValue(`columns[${columnIndex}].rules[${index}].wda011Iconesufixo`,value);}}
													label={translate("Icone de prefixo")}
													// defaultValue={rule.wda011Iconesufixo}
												/>
											</RCCGrid>
											<RCCGrid container item 
												sm={4}
												xs={12}
												styleInline={FieldOnRight}
											>
												<RCCColorPicker
													name={`columns[${columnIndex}].rules[${index}].wda011Iconesufixo`}
													control={control}
													label={translate("Cor do sufixo")}
													labelPosition="start"
													colorPickerProps ={{color:rule.wda011Iconesufixo||"#ffffff0"}}
													defaultValue = {rule.wda011Iconesufixo}
												/>
											</RCCGrid>
										</RCCGrid> */}
									</>
								}
							</RCCGrid>
						))}
					</RCCGrid>				
				</RCCGrid>
			</RCCGrid>
		</Modal>
	);
};

export default forwardRef(ConfigColumnModal);
